import React, { useEffect, useRef, useState } from "react";
import Highlighter from "react-highlight-words";

import { Button, Col, Divider, Input, InputRef, Modal, Row, Space } from "antd";
import { ColumnsType, ColumnType } from "antd/lib/table";
import { SearchOutlined } from "@ant-design/icons";
import { FilterConfirmProps } from "antd/lib/table/interface";

import { useAsyncAxios, utilAxiosWithAuth } from "../utils/customAxios";
import { AlertType, useAlert } from "../provider/AlertProvider";
import DataTable from "../components/DataTable";
import RegisterOption from "../components/RegisterOption";

import * as Request from "../commons/request";
import * as Types from "../commons/typeDTO";
import * as String from "../commons/string";
import * as Utils from "../utils/utils";

type DataIndex = keyof Types.OptionDto;

function OptionList() {
    const alert = useAlert();

    const [options, setOptions] = useState<Array<Types.OptionDto>>();
    const [currentOption, setcurrentOption] = useState<Types.OptionDto>();

    const [editMode, setEditMode] = useState<string>("none");
    const [openDeleteOption, setOpenDeleteOption] = useState(false);

    const [searchText, setSearchText] = useState("");
    const [searchedColumn, setSearchedColumn] = useState("");
    const searchInput = useRef<InputRef>(null);

    const handleSearch = (selectedKeys: string[], confirm: (param?: FilterConfirmProps) => void, dataIndex: DataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText("");
    };

    const getColumnSearchProps = (dataIndex: DataIndex): ColumnType<Types.OptionDto> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: "block" }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        검색
                    </Button>
                    <Button onClick={() => clearFilters && handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                        초기화
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        필터
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        닫기
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ""}
                />
            ) : (
                text
            ),
    });

    const columns: ColumnsType<Types.OptionDto> = [
        {
            align: "center",
            title: String.optionId,
            dataIndex: "optionId",
            key: "optionId",
            ...getColumnSearchProps("optionId"),
        },
        {
            align: "center",
            title: String.optionName,
            dataIndex: "optionName",
            key: "optionName",
            ...getColumnSearchProps("optionName"),
        },
        {
            align: "center",
            title: String.optionPrice,
            dataIndex: "price",
            key: "price",
            ...getColumnSearchProps("price"),
        },
    ];

    const requestAxiosGetOptions = async () => {
        const response = await utilAxiosWithAuth().get(Request.OPTION_LIST_URL);
        console.log(response.data);
        return response.data;
    };

    const {
        loading: loadingGetOptions,
        error: errorGetOptions,
        data: resultGetOptions,
        execute: requestGetOptions,
    } = useAsyncAxios(requestAxiosGetOptions);

    useEffect(() => {
        if (resultGetOptions === null) return;

        setOptions(resultGetOptions.options);
    }, [resultGetOptions]);

    useEffect(() => {
        if (errorGetOptions === null) return;

        alert.setAlert(errorGetOptions.response.data.code, "Error", errorGetOptions.response.data.message);
    }, [errorGetOptions]);

    const requestAxiosRemoveOption = async (option: Types.OptionDto) => {
        const response = await utilAxiosWithAuth().delete(Request.OPTION_URL + "/" + option?.optionId);
        return response.data;
    };

    const {
        loading: loadingRemoveOption,
        error: errorRemoveOption,
        data: resultRemoveOption,
        execute: requestRemoveOptions,
    } = useAsyncAxios(requestAxiosRemoveOption);

    useEffect(() => {
        if (resultRemoveOption === null) return;

        alert.setAlert(AlertType.SUCCESS, "옵션 삭제 성공", "옵션 삭제를 완료하였습니다.");

        requestGetOptions();
    }, [resultRemoveOption]);

    useEffect(() => {
        if (errorRemoveOption === null) return;

        alert.setAlert(errorRemoveOption.response.data.code, "Error", errorRemoveOption.response.data.message);
    }, [errorGetOptions]);

    useEffect(() => {
        requestGetOptions();
    }, []);

    const onRow = (value: Types.OptionDto) => {
        setEditMode("none");
        setcurrentOption(value);
    };

    const handleDeleteOption = () => {
        if (currentOption !== undefined) setOpenDeleteOption(true);
    };

    const handleDeleteOptionOk = () => {
        setOpenDeleteOption(false);
        requestRemoveOptions(currentOption);
    };

    const handleDeleteOptionCancel = () => {
        setOpenDeleteOption(false);
    };

    const onRegisterOption = () => {
        requestGetOptions();
        setEditMode("none");
    };

    return (
        <>
            <Row justify={"space-between"}>
                <Col span={6}>
                    <Divider orientation="left">
                        <span>옵션</span>
                    </Divider>
                </Col>
                <Space>
                    <Button
                        onClick={() => {
                            editMode === "add" ? setEditMode("none") : setEditMode("add");
                        }}
                    >
                        {String.userAdd}
                    </Button>
                    <Button
                        onClick={() => {
                            editMode === "update" ? setEditMode("none") : setEditMode("update");
                        }}
                    >
                        {String.userUpdate}
                    </Button>
                    <Button onClick={() => handleDeleteOption()} danger>
                        {String.userRemove}
                    </Button>
                </Space>
            </Row>
            <DataTable hasScroll={false} columns={columns} rowKey="optionId" dataSource={options} onRow={onRow} />

            {editMode === "add" && <RegisterOption editMode={editMode} onRegisterOption={onRegisterOption} />}

            {editMode !== "add" && currentOption !== undefined && (
                <RegisterOption editMode={editMode} option={currentOption} onRegisterOption={onRegisterOption} />
            )}

            {openDeleteOption && (
                <Modal open={openDeleteOption} onOk={handleDeleteOptionOk} onCancel={handleDeleteOptionCancel}>
                    {currentOption?.optionName} 삭제하시겠습니까?
                </Modal>
            )}
        </>
    );
}

export default OptionList;
